import {
  SystemStyleObject,
  createMultiStyleConfigHelpers,
} from '@chakra-ui/react';
import { inputAnatomy } from '@chakra-ui/anatomy';
import {
  ColorMode,
  getTypographyToken,
  getBorderWidthToken,
  getBoxSizingStyles,
} from '@allergan-data-labs/alle-elements-core';

const chakraSelectIconWrapperSelector = '& + .chakra-select__icon-wrapper';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(inputAnatomy.keys);

const commonElementStyles = (colorMode: ColorMode) => ({
  minWidth: '24',
  height: '100%',
  color: `${colorMode}.Icon/Neutral/Default`,
});

export const getLeftElementStyles = ({
  colorMode = 'light',
}: {
  colorMode: ColorMode;
}) => ({
  ...commonElementStyles(colorMode),
  marginLeft: '12',
});

export const getRightElementStyles = ({
  colorMode = 'light',
}: {
  colorMode: ColorMode;
}): SystemStyleObject => ({
  ...commonElementStyles(colorMode),
  marginRight: '12',
});

export const getErrorIconStyles = (colorMode: ColorMode) => ({
  color: `${colorMode}.Icon/Status/Error medium 3`,
  height: '20px',
  width: '20px',
  borderRadius: '100%',
});

export const getBaseStyles = (colorMode: ColorMode): SystemStyleObject => {
  return {
    ...getBoxSizingStyles(),
    paddingX: '16',
    paddingY: '12',
    color: `${colorMode}.Text/Neutral/Default`,
    bg: `${colorMode}.Container/Neutral/Base`,
    borderColor: `${colorMode}.Border/Neutral/Text field/Enable`,
    borderWidth: 'Extra light',
    borderRadius: 'Small',
    ...getTypographyToken('Body/Medium/Regular'),
    '::placeholder': {
      color: `${colorMode}.Text/Neutral/Placeholder`,
      opacity: 1,
    },
    _hover: {
      borderWidth: 'Extra light',
      borderColor: `${colorMode}.Border/Neutral/Text field/Hover`,
    },
    _focus: {
      outline: 'none',
    },
    _focusVisible: {
      borderColor: `${colorMode}.Border/Neutral/Text field/Hover`,
      borderWidth: 'Extra light',
      outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
      outlineStyle: 'solid',
      outlineWidth: getBorderWidthToken('Bold') || '4px',
      outlineOffset: '0px',
      boxShadow: 'none',
    },
    _disabled: {
      backgroundColor: `${colorMode}.Container/Neutral/Disabled/Default`,
      borderWidth: 'Extra light !important',
      borderColor: `${colorMode}.Border/Neutral/Disabled/Default`,
      color: `${colorMode}.Text/Neutral/Disabled/Default`,
      opacity: 1,
      cursor: 'not-allowed',
      '::placeholder': {
        color: `${colorMode}.Text/Neutral/Disabled/Default`,
      },
      [chakraSelectIconWrapperSelector]: {
        color: `${colorMode}.Text/Neutral/Disabled/Default`,
        opacity: 1,
      },
    },
    _invalid: {
      color: `${colorMode}.Text/Status/Error medium 3`,
      borderColor: `${colorMode}.Border/Status/Error medium 3`,
      '::placeholder': {
        color: `${colorMode}.Text/Status/Error medium 3`,
      },
      [chakraSelectIconWrapperSelector]: {
        color: `${colorMode}.Text/Status/Error medium 3`,
      },
    },
    _readOnly: {
      bg: `${colorMode}.Container/Neutral/Mute 1`,
      color: `${colorMode}.Text/Neutral/Default`,
      '::placeholder': {
        textColor: `${colorMode}.Container/Neutral/Mute 1`,
      },
      [chakraSelectIconWrapperSelector]: {
        color: `${colorMode}.Text/Neutral/Disabled/Default`,
      },
    },
  };
};

interface InputFieldStylingProps {
  leftOffset?: number;
  rightOffset?: number;
  colorMode: ColorMode;
  removeFocusBorder?: boolean;
}

export const getAdditionalInputFieldStyles = ({
  leftOffset,
  rightOffset,
  colorMode,
  removeFocusBorder,
}: InputFieldStylingProps) => {
  return {
    ...(leftOffset === undefined && {
      color: `transparent`,
      '::placeholder': {
        color: `transparent`,
      },
    }),
    ...(leftOffset &&
      leftOffset > 0 && { paddingLeft: `${leftOffset + 20}px` }),
    ...(rightOffset &&
      rightOffset > 0 && { paddingRight: `${rightOffset + 20}px` }),
    _focus: {
      outlineColor: removeFocusBorder
        ? 'transparent'
        : `${colorMode}.Border/Brand/Focus ring/Default`,
    },
  };
};

export const InputTheme = defineMultiStyleConfig({
  baseStyle: definePartsStyle((props) => {
    const { colorScheme } = props;
    return {
      field: getBaseStyles(colorScheme as ColorMode),
    };
  }),
  defaultProps: {
    size: 'custom',
    variant: 'custom',
  },
});
